import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { 
    RefreshCw, AlertTriangle, CheckCircle, XCircle, Clock, 
    Wifi, WifiOff, Link2, Zap, History, Search
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import logger from '@/lib/logger';

const STATUS_CFG = {
    no_data: { label: 'No Data', color: 'bg-red-500', textColor: 'text-red-600' },
    partial: { label: 'Partial', color: 'bg-amber-500', textColor: 'text-amber-600' },
    good: { label: 'Good', color: 'bg-emerald-500', textColor: 'text-emerald-600' },
};

const AttendanceGapPage = () => {
    const [gapData, setGapData] = useState(null);
    const [syncLogs, setSyncLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [mapping, setMapping] = useState(false);
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState('gap');
    const now = new Date();
    const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);

    const monthOptions = [];
    for (let i = 0; i < 6; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        monthOptions.push({
            value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
            label: d.toLocaleString('default', { month: 'long', year: 'numeric' })
        });
    }

    const fetchGap = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/hr/attendance-gap?month=${month}`);
            setGapData(res.data);
        } catch (e) { logger.error(e); toast.error('Failed to load gap analysis'); } finally { setLoading(false); }
    };

    const fetchLogs = async () => {
        try {
            const res = await api.get('/hr/biocloud/sync-logs?limit=20');
            setSyncLogs(res.data);
        } catch (e) { logger.error(e); }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchGap(); fetchLogs(); }, [month]);

    const triggerSync = async () => {
        setSyncing(true);
        try {
            const res = await api.post('/hr/biocloud/trigger-sync');
            if (res.data.success) {
                toast.success(`Synced ${res.data.synced} records`);
            } else {
                toast.warning(res.data.message || 'Sync completed with issues');
            }
            fetchGap();
            fetchLogs();
        } catch (e) {
            toast.error('Sync failed');
        } finally { setSyncing(false); }
    };

    const triggerAutoMap = async () => {
        setMapping(true);
        try {
            const res = await api.post('/hr/biocloud/auto-map');
            toast.success(res.data.message);
            fetchGap();
        } catch (e) {
            toast.error('Auto-map failed');
        } finally { setMapping(false); }
    };

    const filtered = (gapData?.gap_analysis || []).filter(g => {
        if (!search) return true;
        const q = search.toLowerCase();
        return g.name?.toLowerCase().includes(q) || g.department?.toLowerCase().includes(q) || g.employee_code?.toLowerCase().includes(q);
    });

    if (loading && !gapData) {
        return <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
    }

    return (
        <div className="space-y-5" data-testid="attendance-gap-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Attendance Data & Sync</h1>
                    <p className="text-sm text-muted-foreground">Gap analysis, BioCloud sync status, and employee mapping</p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={month} onValueChange={setMonth}>
                        <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={triggerAutoMap} disabled={mapping} data-testid="auto-map-btn">
                        {mapping ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                        <span className="ml-1">Auto-Map</span>
                    </Button>
                    <Button size="sm" onClick={triggerSync} disabled={syncing} data-testid="trigger-sync-btn">
                        {syncing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                        <span className="ml-1">Sync Now</span>
                    </Button>
                </div>
            </div>

            {gapData && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold">{gapData.total_employees}</p><p className="text-xs text-muted-foreground">Total Employees</p></CardContent></Card>
                    <Card className="border-l-4 border-l-red-500"><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-red-600">{gapData.no_data}</p><p className="text-xs text-muted-foreground">No Data</p></CardContent></Card>
                    <Card className="border-l-4 border-l-amber-500"><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-amber-600">{gapData.partial_data}</p><p className="text-xs text-muted-foreground">Partial</p></CardContent></Card>
                    <Card className="border-l-4 border-l-emerald-500"><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-emerald-600">{gapData.full_data}</p><p className="text-xs text-muted-foreground">Complete</p></CardContent></Card>
                    <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-blue-600">{gapData.mapped_to_biocloud}</p><p className="text-xs text-muted-foreground">BioCloud Mapped</p></CardContent></Card>
                    <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-slate-500">{gapData.unmapped}</p><p className="text-xs text-muted-foreground">Unmapped</p></CardContent></Card>
                </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="gap">Employee Gap Analysis</TabsTrigger>
                    <TabsTrigger value="sync-log" data-testid="sync-log-tab">Sync History</TabsTrigger>
                    <TabsTrigger value="schedule">Auto-Sync Schedule</TabsTrigger>
                </TabsList>

                <TabsContent value="gap" className="mt-4">
                    <Card>
                        <CardHeader className="pb-3 flex-row items-center justify-between gap-4">
                            <CardTitle className="text-base">Data Coverage — {month}</CardTitle>
                            <div className="relative">
                                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 w-[200px]" />
                            </div>
                        </CardHeader>
                        <ScrollArea className="max-h-[500px]">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="sticky left-0 bg-background z-10">Employee</TableHead>
                                        <TableHead>Department</TableHead>
                                        <TableHead className="text-center">BioCloud</TableHead>
                                        <TableHead className="text-center">Attendance Days</TableHead>
                                        <TableHead className="text-center">Expected</TableHead>
                                        <TableHead>Coverage</TableHead>
                                        <TableHead className="text-center">Status</TableHead>
                                        <TableHead>Issues</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map(g => (
                                        <TableRow key={g.employee_id} className={g.status === 'no_data' ? 'bg-red-50/30 dark:bg-red-950/10' : g.status === 'partial' ? 'bg-amber-50/30 dark:bg-amber-950/10' : ''}>
                                            <TableCell className="sticky left-0 bg-background z-10">
                                                <p className="font-medium text-sm">{g.name}</p>
                                                <p className="text-[10px] text-muted-foreground font-mono">{g.employee_code}</p>
                                            </TableCell>
                                            <TableCell className="text-sm">{g.department}</TableCell>
                                            <TableCell className="text-center">
                                                {g.biocloud_mapped ? (
                                                    <Badge className="bg-emerald-500 text-white text-[10px]">{g.biocloud_code}</Badge>
                                                ) : (
                                                    <Badge variant="destructive" className="text-[10px]">Not Mapped</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-center font-bold">{g.attendance_days}</TableCell>
                                            <TableCell className="text-center text-muted-foreground">{g.expected_days}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2 min-w-[100px]">
                                                    <Progress value={g.coverage_pct} className="h-2 flex-1" />
                                                    <span className="text-xs font-mono w-10">{g.coverage_pct}%</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <Badge className={`${STATUS_CFG[g.status]?.color || 'bg-slate-400'} text-white text-[10px]`}>
                                                    {STATUS_CFG[g.status]?.label || g.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="space-y-0.5">
                                                    {(g.reasons || []).map((r, i) => (
                                                        <p key={`${g.date}-reason-${i}`} className="text-[10px] text-muted-foreground flex items-center gap-1">
                                                            <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" /> {r}
                                                        </p>
                                                    ))}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </ScrollArea>
                    </Card>
                </TabsContent>

                <TabsContent value="sync-log" className="mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2"><History className="h-5 w-5" /> Sync History</CardTitle>
                        </CardHeader>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Timestamp</TableHead>
                                    <TableHead>Method</TableHead>
                                    <TableHead className="text-center">Records</TableHead>
                                    <TableHead className="text-center">Synced</TableHead>
                                    <TableHead className="text-center">Status</TableHead>
                                    <TableHead>Message</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {syncLogs.length === 0 ? (
                                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No sync history yet</TableCell></TableRow>
                                ) : syncLogs.map((log, i) => (
                                    <TableRow key={log.id || log.timestamp || `log-${i}`}>
                                        <TableCell className="text-xs font-mono">{log.timestamp?.replace('T', ' ')?.slice(0, 19)}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="text-xs">
                                                {log.method === 'auto_sync' ? 'Auto' : log.method === 'manual_trigger' ? 'Manual' : log.method === 'file_upload' ? 'CSV' : log.method}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-center">{log.total_rows || 0}</TableCell>
                                        <TableCell className="text-center font-medium">{(log.synced_new || 0) + (log.synced_updated || 0)}</TableCell>
                                        <TableCell className="text-center">
                                            {log.success ? (
                                                <CheckCircle className="h-4 w-4 text-emerald-500 mx-auto" />
                                            ) : (
                                                <XCircle className="h-4 w-4 text-red-500 mx-auto" />
                                            )}
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground max-w-[250px] truncate">{log.message || log.filename || '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Card>
                </TabsContent>

                <TabsContent value="schedule" className="mt-4">
                    <div className="space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">Auto-Sync Schedule</CardTitle>
                                <CardDescription>BioCloud attendance is automatically synced at the following UAE times</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {['08:00 AM', '12:00 PM', '04:00 PM', '08:00 PM'].map((time) => (
                                        <div key={time} className="p-4 rounded-lg border bg-muted/30 text-center">
                                            <Clock className="h-5 w-5 mx-auto mb-2 text-blue-500" />
                                            <p className="font-bold">{time}</p>
                                            <p className="text-xs text-muted-foreground">UAE Time (UTC+4)</p>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-4 p-4 rounded-lg bg-muted/40 border text-sm space-y-1">
                                    <p className="font-medium">How Auto-Sync works:</p>
                                    <ul className="list-disc list-inside text-muted-foreground space-y-0.5 text-xs">
                                        <li>Runs every 4 hours at the times above (UAE timezone)</li>
                                        <li>First auto-maps unmapped employees by name</li>
                                        <li>Then attempts to pull attendance data from BioCloud device API</li>
                                        <li>If API is unreachable, logs the failure — no data is lost</li>
                                        <li>For immediate data, use the <strong>CSV Upload</strong> on the BioCloud Sync page</li>
                                        <li>Manual sync can be triggered anytime using the <strong>Sync Now</strong> button</li>
                                    </ul>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default AttendanceGapPage;
